import { useEffect, useState } from 'react'
import Head from 'next/head'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'
import FractionDraggableCard from '~/bootcamp/Activities/Fractions/components/FractionDraggableCard'
import DragDroppableContainer from '~/bootcamp/Activities/Fractions/components/DragDropContainer'
import { shuffle } from '~/utils'

const FRACTION_VALUES = [2, 3, 4, 5, 10]

function UnitFractionOrder({
  answerState,
  onNextQuestionClick,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC: SubmitButton,
}: ActivityProps): React.ReactElement {
  const [answer, setAnswer] = useState<number[]>([])
  const [taskHistory, setTaskHistory] = useState<number>(0)
  const [shuffledValues, setShuffledValues] = useState(shuffle(FRACTION_VALUES.slice(0, 3)))
  const [containerKey, setContainerKey] = useState(1)

  // The sorted order (the answer) stays out — emit the on-screen, shuffled draw order, never sorted.
  useEmitInstance(
    onInstanceChange,
    shuffledValues.length ? shuffledValues.map((value) => `\\frac{1}{${value}}`).join(', ') : null,
  )

  useEffect(() => {
    setContainerKey((ck) => ck + 1)
    setAnswer([])
    setShuffledValues(shuffle(FRACTION_VALUES.filter((value) => value !== taskHistory)).slice(0, 3))
  }, [taskHistory, difficultyLevel])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    let isCorrect = false
    const sortedValues: number[] = [...shuffledValues]
    // eslint-disable-next-line fp/no-mutating-methods
    sortedValues.sort((a, b) => a - b)
    const validAnswerLength = answer.filter((value) => value !== undefined && value !== -1).length
    if (
      validAnswerLength === 3 &&
      ((difficultyLevel === 1 &&
        answer[sortedValues[0]] > answer[sortedValues[1]] &&
        answer[sortedValues[1]] > answer[sortedValues[2]]) ||
        (difficultyLevel === 2 &&
          answer[sortedValues[0]] < answer[sortedValues[1]] &&
          answer[sortedValues[1]] < answer[sortedValues[2]]))
    ) {
      isCorrect = true
    }
    onAnswerStateChange(isCorrect)
  }

  const onDrop = (data: string, isOnTop: boolean, index: number): void => {
    const _answer = [...answer]
    _answer[parseInt(data)] = isOnTop ? -1 : index
    setAnswer(_answer)
  }

  const resetAnswer = (): void => {
    setContainerKey(containerKey + 1)
    setAnswer([])
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory(shuffledValues[0])
    onAnswerStateChange(null)
    onNextQuestionClick()
  }

  return (
    <>
      <Head>
        {/* enable dragging on mobile and touch devices */}
        <script id="DragDropTouch" src="/assets/bootcamp/DragDropTouch.js" type="text/javascript" />
      </Head>

      <ActivityWrapper>
        <FractionsActivityWrapper>
          <div key={containerKey}>
            <div className="flex flex-row mb-5">
              {shuffledValues.map((value, index) => (
                <DragDroppableContainer
                  key={value}
                  onDrop={(data) => {
                    onDrop(data, true, index)
                  }}
                >
                  <FractionDraggableCard denominator={value} id={`${value}`} numerator={1} />
                </DragDroppableContainer>
              ))}
            </div>
            <div className="flex flex-row">
              {shuffledValues.map((value, index) => (
                <DragDroppableContainer
                  isDanger={answerState === false}
                  key={value}
                  onDrop={(data) => {
                    onDrop(data, false, index)
                  }}
                />
              ))}
            </div>
          </div>
        </FractionsActivityWrapper>
        {SubmitButton && (
          <SubmitButton
            clearFormAction={() => resetAnswer()}
            correctAnswer={answerState ?? false}
            formValid={answer.length !== 0}
            goToNextQuestionAction={handleOnNextQuestionClick}
            submitAnswerAction={handleOnCheckAnswerClick}
          />
        )}
      </ActivityWrapper>
    </>
  )
}

export default UnitFractionOrder
